"use client";

import { motion, useInView, AnimatePresence } from "framer-motion";
import { useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

const faqs = [
    {
        question: "what does burg ai intentionally ignore?",
        answer:
            "formatting-only changes, import reordering, and cosmetic updates. we focus on logic bugs, security vulnerabilities, architectural issues, and performance problems. no noise, only signal.",
    },
    {
        question: "when should i not use burg ai?",
        answer:
            "never use us for trivial prs under 10 lines, pure documentation updates, or when you need human judgment for complex business logic. burg ai is for catching technical debt and bugs, not replacing human code review culture.",
    },
    {
        question: "how is this different from copilot pr reviews?",
        answer:
            "copilot suggests code as you write. burg ai reviews entire prs for bugs, security issues, and architectural problems. copilot prevents mistakes during writing. burg ai catches them before merge. we complement each other perfectly.",
    },
    {
        question: "will this slow down merges?",
        answer:
            "no. reviews complete in 2-5 seconds and run asynchronously. your pr workflow continues normally. burg ai comments appear immediately, giving you instant feedback without blocking your pipeline.",
    },
    {
        question: "what is byok and why does it matter?",
        answer:
            "byok = bring your own key. you provide your own api keys from openai, anthropic, gemini, or openrouter. your data never touches our servers. you control costs. you can switch providers anytime. no vendor lock-in.",
    },
    {
        question: "how much does it cost?",
        answer:
            "burg ai is free to use. you only pay for your llm provider's api costs. free plan: 100 reviews/month, 10 reviews/day, 25 files per pr. pro plan: $9/month for unlimited reviews and advanced features. since you bring your own keys, you have complete cost transparency.",
    },
];

function FAQItem({
    faq,
    index,
    isInView,
}: {
    faq: (typeof faqs)[0];
    index: number;
    isInView: boolean;
}) {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
            className="border-b border-border last:border-b-0"
        >
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full py-6 flex items-center justify-between gap-4 text-left hover:opacity-70 transition-opacity"
            >
                <span className="text-base font-medium">{faq.question}</span>
                <motion.div
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                >
                    <ChevronDown className="h-5 w-5 flex-shrink-0 text-muted-foreground" />
                </motion.div>
            </button>
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                    >
                        <p className="pb-6 text-muted-foreground text-sm leading-relaxed">
                            {faq.answer}
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
}

export function FAQSection() {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });

    return (
        <section ref={ref} className="py-24 px-4 bg-background">
            <div className="container mx-auto max-w-3xl">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <h2 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight">
                        frequently asked
                    </h2>
                    <p className="text-muted-foreground text-lg">
                        everything you need to know
                    </p>
                </motion.div>

                <div className="border border-border rounded-xl p-6 md:p-8 bg-card">
                    {faqs.map((faq, index) => (
                        <FAQItem key={index} faq={faq} index={index} isInView={isInView} />
                    ))}
                </div>
            </div>
        </section>
    );
}
